export const DOSE_UNITS = [
  // Mass / volume strengths (true UCUM units)
  { value: 'mg', label: 'mg', ucumCode: 'mg', ucum: true, volume: false },
  { value: 'g', label: 'g', ucumCode: 'g', ucum: true, volume: false },
  { value: 'mcg', label: 'mcg', ucumCode: 'ug', ucum: true, volume: false },
  { value: 'ng', label: 'ng', ucumCode: 'ng', ucum: true, volume: false },
  { value: 'mL', label: 'mL', ucumCode: 'mL', ucum: true, volume: true },
  { value: 'L', label: 'L', ucumCode: 'L', ucum: true, volume: true },
  { value: 'meq', label: 'mEq', ucumCode: 'meq', ucum: true, volume: false },
  { value: 'IU', label: 'IU', ucumCode: '[iU]', ucum: true, volume: false },
  { value: '%', label: '%', ucumCode: '%', ucum: true, volume: false },
  // Administration forms (counted units — no asserted UCUM system/code)
  {
    value: 'tablet',
    label: 'tablet(s)',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
  {
    value: 'capsule',
    label: 'capsule(s)',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
  { value: 'drop', label: 'drop(s)', ucumCode: '', ucum: false, volume: false },
  { value: 'puff', label: 'puff(s)', ucumCode: '', ucum: false, volume: false },
  {
    value: 'spray',
    label: 'spray(s)',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
  {
    value: 'application',
    label: 'application(s)',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
  {
    value: 'inhalation',
    label: 'inhalation(s)',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
  {
    value: 'applicatorful',
    label: 'applicatorful(s)',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
  {
    value: 'packet',
    label: 'packet(s)',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
  { value: 'unit', label: 'unit(s)', ucumCode: '', ucum: false, volume: false },
  // Explicitly "we don't know", so a value can be saved without inventing a
  // unit for it. Carries no UCUM code and is not a counted form.
  {
    value: 'unknown',
    label: 'Unknown',
    ucumCode: '',
    ucum: false,
    volume: false,
  },
] as const;

export type DoseUnit = (typeof DOSE_UNITS)[number]['value'];

/** The "we don't know" member of DOSE_UNITS, which most rules exclude. */
export const UNKNOWN_UNIT = 'unknown';

/** How an unknown unit reads in a summary: parenthesised, never pluralised. */
export const UNKNOWN_UNIT_TEXT = '(unit unknown)';

type UnitDef = { value: string; label: string };

/**
 * Units that measure the drug itself (mg, mL, %). These belong to a product's
 * strength, not to what the patient takes.
 */
export const MEASURED_UNITS: readonly UnitDef[] = DOSE_UNITS.filter(
  (u) => u.ucum
);

/**
 * Counted administration forms (tablet, puff). These belong to the quantity
 * taken. `unknown` is excluded: it is a counted form only by the absence of a
 * UCUM code.
 */
export const COUNTED_UNITS: readonly UnitDef[] = DOSE_UNITS.filter(
  (u) => !u.ucum && u.value !== UNKNOWN_UNIT
);

const unknownUnit = DOSE_UNITS.filter((u) => u.value === UNKNOWN_UNIT);

/**
 * Measured units a patient is dosed in directly: a liquid is taken as "5 mL",
 * insulin as "10 IU". They sit in both families, since they describe a strength
 * as readily as a quantity.
 *
 * mg is deliberately not one of them. A milligram is always a strength, and
 * reaching for it in the quantity field is the habit this split exists to break.
 */
const DOSABLE_MEASURED = ['mL', 'L', 'IU'];
const DOSABLE_MEASURED_UNITS: readonly UnitDef[] = MEASURED_UNITS.filter((u) =>
  DOSABLE_MEASURED.includes(u.value)
);

/**
 * Which units each field offers. Splitting them is the point: curators used to
 * see mg in the quantity dropdown and reach for it out of habit, which recorded
 * a strength where the count taken belongs. The form now only offers the units
 * that make sense for the field.
 */
export const STRENGTH_UNITS: readonly UnitDef[] = [
  ...MEASURED_UNITS,
  ...unknownUnit,
];
export const QUANTITY_UNITS: readonly UnitDef[] = [
  ...COUNTED_UNITS,
  ...DOSABLE_MEASURED_UNITS,
  ...unknownUnit,
];
/** "250 mg per 5 mL" and "875 mg per 1 tablet" are both real, so both sets. */
export const STRENGTH_PER_UNITS: readonly UnitDef[] = [
  ...MEASURED_UNITS,
  ...COUNTED_UNITS,
];

/**
 * What the Form dropdown offers. One pick names the thing the product comes in,
 * and that noun is then the only one on the form: it is the denominator of every
 * strength row ("875 mg per 1 tablet") and the unit of the dose ("2 tablets").
 *
 * Derived rather than declared. VEG-760's unit lists are a spec shared with
 * Medly (VMD-713), written per field — a Quantity list and a Strength "per"
 * list — and folding two controls into one here must not make that spec
 * uncheckable. `QUANTITY_UNITS` is what the dose can be counted in, which is
 * what the Form has to offer; the "per" list is wider only because a stored
 * denominator can be a measured unit the Form itself never sets.
 */
export const FORM_UNITS: readonly UnitDef[] = QUANTITY_UNITS;
/** An infusion rate is always measured: "100 mL per hour". */
export const RATE_UNITS: readonly UnitDef[] = MEASURED_UNITS;
/**
 * A ceiling mirrors the quantity it caps: "max 8 tablets per day", "max 30 mL
 * per day". No `unknown`: a ceiling in units nobody can name is not a ceiling.
 *
 * No longer backs a control — the ceiling takes its noun from Form, like the
 * strength and the dose. Kept because it is one of the five field lists in
 * VEG-760's shared spec, and asserted against what the form can actually write
 * in `medication-dosage.spec.ts`, so the spec is checked rather than described.
 */
export const MAX_DOSE_UNITS: readonly UnitDef[] = [
  ...COUNTED_UNITS,
  ...DOSABLE_MEASURED_UNITS,
];

/**
 * Whether a unit measures the drug itself (mg, mL, %) rather than counting what
 * the patient is handed (tablet, puff).
 *
 * The form asks it of the chosen dose form: a measured form is a concentration,
 * so its strength needs a "per how much" amount ("each 5 mL contains 250 mg"),
 * where a counted form is always per one and the input would be noise.
 */
export function isMeasuredUnit(unit: string): boolean {
  return DOSE_UNITS.find((u) => u.value === unit)?.ucum === true;
}

/**
 * Whether a dose form is quoted as a volume, so its strength is a concentration
 * and needs a "per how much" amount ("each 5 mL contains 250 mg").
 *
 * Narrower than `isMeasuredUnit`, which is what this used to key on: IU is a
 * measured unit but insulin isn't dosed "per 5 IU", and a legacy unrecognised
 * form that happens to be measured pulled the input up for no reason. Matches
 * Medly's `formQuotesAVolume`.
 */
export function isVolumeUnit(unit: string): boolean {
  return DOSE_UNITS.find((u) => u.value === unit)?.volume === true;
}

/**
 * Dropdown options for a unit field.
 *
 * `stored` is the value already on the record, appended when the field's own set
 * doesn't offer it. Legacy records routinely carry a unit this field no longer
 * offers — a dose of "10 mg" predates the measured/counted split — and without
 * it the dropdown renders blank against a value that is still in the resource,
 * so the curator can't see what is stored and the next save writes over it.
 */
export function unitOptions(
  units: readonly UnitDef[],
  stored?: string
): { k: string; v: string }[] {
  const options = units.map((u) => ({ k: u.value, v: u.label }));
  if (stored && !options.some((o) => o.k === stored)) {
    options.push({ k: stored, v: stored });
  }
  return options;
}

/**
 * A unit as it reads in a sentence. Counted forms pluralise ("2 tablets");
 * measured units are invariant; `unknown` becomes a parenthetical so it never
 * reads as a real unit ("10 (unit unknown)" rather than "10 unknowns").
 */
export function formatUnitLabel(
  unit: string,
  count: number | null | undefined
): string {
  if (!unit) return '';
  if (unit === UNKNOWN_UNIT) return UNKNOWN_UNIT_TEXT;
  const def = DOSE_UNITS.find((u) => u.value === unit);
  const isCounted = def ? !def.ucum : false;
  return isCounted && count !== 1 ? `${unit}s` : unit;
}

/**
 * Schedule presets. "As needed" is deliberately absent: PRN is additive to the
 * schedule (a separate toggle mapping to `asNeededBoolean`), not a schedule of
 * its own. `count` presets emit `timing.repeat.count` instead of a
 * frequency/period pair.
 */
export const FREQUENCY_PRESETS = [
  {
    value: 'once_daily',
    label: 'Once daily',
    frequency: 1,
    period: 1,
    periodUnit: 'd',
    count: null,
  },
  {
    value: 'twice_daily',
    label: 'Twice daily',
    frequency: 2,
    period: 1,
    periodUnit: 'd',
    count: null,
  },
  {
    value: 'three_times_daily',
    label: 'Three times daily',
    frequency: 3,
    period: 1,
    periodUnit: 'd',
    count: null,
  },
  {
    value: 'every_12_hours',
    label: 'Every 12 hours',
    frequency: 1,
    period: 12,
    periodUnit: 'h',
    count: null,
  },
  {
    value: 'every_other_day',
    label: 'Every other day',
    frequency: 1,
    period: 2,
    periodUnit: 'd',
    count: null,
  },
  {
    value: 'once_weekly',
    label: 'Once weekly',
    frequency: 1,
    period: 1,
    periodUnit: 'wk',
    count: null,
  },
  {
    value: 'once_monthly',
    label: 'Once monthly',
    frequency: 1,
    period: 1,
    periodUnit: 'mo',
    count: null,
  },
  {
    value: 'one_time_only',
    label: 'One time only',
    frequency: null,
    period: null,
    periodUnit: null,
    count: 1,
  },
  {
    value: 'custom',
    label: 'Custom',
    frequency: null,
    period: null,
    periodUnit: null,
    count: null,
  },
] as const;

export type FrequencyPresetValue = (typeof FREQUENCY_PRESETS)[number]['value'];

export const FREQUENCY_PERIOD_UNITS = [
  { value: 'h', label: 'hour(s)' },
  { value: 'd', label: 'day(s)' },
  { value: 'wk', label: 'week(s)' },
  { value: 'mo', label: 'month(s)' },
] as const;

export const TIMING_OPTIONS = [
  { value: 'MORN', label: 'Morning' },
  { value: 'AFT', label: 'Afternoon' },
  { value: 'EVE', label: 'Evening' },
  { value: 'HS', label: 'At bedtime' },
  { value: 'AC', label: 'Before meals' },
  { value: 'PC', label: 'After meals' },
] as const;

export type TimingCode = (typeof TIMING_OPTIONS)[number]['value'];

/** Noun form of a period unit, for human-readable frequency phrases. */
export const PERIOD_UNIT_NOUNS: Record<string, string> = {
  h: 'hour',
  d: 'day',
  wk: 'week',
  mo: 'month',
  a: 'year',
};

export interface FrequencyRepeat {
  frequency?: number | null;
  period?: number | null;
  periodMax?: number | null;
  periodUnit?: string | null;
  count?: number | null;
}

/**
 * Derives a frequency preset from structured `timing.repeat` fields.
 *
 * PRN is not consulted: "as needed" is additive to the schedule, so a PRN
 * record still resolves to whatever schedule it carries. Callers pass the
 * effective count in `frequency` — for PRN records that is `frequencyMax`.
 *
 * Returns '' when there is no schedule at all, and 'custom' when a schedule is
 * present but no preset describes it (including any interval range, which no
 * preset can express).
 */
export function getFrequencyPreset({
  frequency,
  period,
  periodMax,
  periodUnit,
  count,
}: FrequencyRepeat): FrequencyPresetValue | '' {
  // A single administration is a preset in its own right, so it is settled
  // before the interval test: a stored `periodMax` of 0 would otherwise drag it
  // to custom in one app and not the other.
  if (count === 1 && !frequency && !period) return 'one_time_only';
  // An interval range ("every 4 to 6 hours") is always custom — matching a
  // preset here would silently drop periodMax on the next save. A null check,
  // not a truthy one, so a stored 0 reads as a real bound in both apps.
  if (periodMax !== null && periodMax !== undefined) return 'custom';
  if (
    frequency === undefined ||
    frequency === null ||
    period === undefined ||
    period === null
  ) {
    return count ? 'custom' : '';
  }
  const match = FREQUENCY_PRESETS.find(
    (p) =>
      p.frequency === frequency &&
      p.period === period &&
      p.periodUnit === periodUnit
  );
  return (match?.value as FrequencyPresetValue | null) ?? 'custom';
}
